<template>
    <div id="app" class="app">
        <header v-if="showNavigation">
            <nav class="main-nav-wrapper">
                <div>
                    <router-link to="/" class="nav-link">
                        <img class="wc-lock" src="./assets/warchest_logo_hq.png" />
                    </router-link>
                </div>
                <div class="main-nav">
                    <nav-menu />
                </div>
            </nav>
        </header>
        <main class="main">
            <router-view />
        </main>
        <notifications group="app" class="notification" position="bottom right"/>
        <!-- <notifications group="rank" class="notification rank-notification" position="bottom right"/> -->
		<notifications
			group="rank"
			class="notification rank-notification"
			position="bottom right"
			>
			<template #body="{ item }">
				<div class="notification-title">
					{{ item.title }}
				</div>
				<div class="notification-content">
					<div
						class="notification-text"
						v-html="item.text"
					/>
				</div>
			</template>
		</notifications>
        <modal
            :message="leaveGameModal.message"
            :options="leaveGameModal.options"
            :show="showModal('leaveGame')"
            type="leaveGame"
        />
        <modal
            :message="leaveAllGamesModal.message"
            :options="leaveAllGamesModal.options"
            :show="showModal('leaveAllGames')"
            type="leaveAllGames"
        />
		<terms-modal
			:show="showModal('termsAndConditions')"
			:onCloseCallback="closeTermsAndConditionsModal"
			type="termsAndConditions"
		/>
        <footer class="footer" v-if="showNavigation">
            <div>
                <span class="footer-text">&copy; {{year}} Morgan Polak</span>
                <router-link to="/contact-us" class="nav-link-icon">
                    <fa-icon icon="envelope" />
                </router-link>
            </div>
            <div class="copyright-notice">
                The War Chest title, logo, concept, rules, unit coin graphics, and card descriptions,
                are the intellectual property of Alderac Entertainment Group and the game creators,
                Trevor Benjamin and David Thompson. The visual assets were created by
                Brigette Indelicato.
            </div>
			<div class="terms-and-conditions-notice">
				By using this website you agree to our
				 <span class="link" @click="openTermsAndConditionsModal">
					Terms & Conditions</span>.
			</div>
        </footer>
        <div class="direct-messages-wrapper" v-if="userId">
            <button
                class="btn btn-3d btn-round btn-outline btn-gray"
                v-if="!directMessagesExpanded && showNavigation"
                @click="expandDirectMessages()"
            >
                <fa-icon icon="comments" />
                <span class="unread-messages-count" v-if="allUnreadMessages.length">
                    {{ allUnreadMessages.length > 9 ? '9+' : allUnreadMessages.length }}
                </span>
            </button>
            <direct-messages-widget />
        </div>
    </div>
</template>

<script>
import moment from 'moment';

import { loginInitialState } from './components/Login/Login-state';
import { getAllUnreadMessages } from './components/DirectMessagesWidget/DirectMessagesWidget-helpers';

export default {
    name: 'App',
    mounted() {
        const { sessionExp, userId } = this.$store.state.loginState;
        const sessionExpired = (sessionExp && moment().isAfter(moment(sessionExp)));

        if (sessionExp && userId && sessionExpired) {
            this.$store.dispatch('setAuthDetails', loginInitialState);
            this.$router.push('/');
        } else if (userId) {
            this.$socket.emit('joinDirectmessages', userId);
        }

        window.addEventListener('storage', this.onStorageEvent);
    },
    beforeUnmount() {
        window.removeEventListener('storage', this.onStorageEvent);
    },
    data() {
        return {
        };
    },
    computed: {
        showNavigation() {
            return !(['game', 'replay'].includes(this.$route.name));
        },
        activeConversation() {
            return this.$store.state.directMessagesState.activeConversation;
        },
        allUnreadMessages() {
            return getAllUnreadMessages(this.conversations, this.userId, this.username);
        },
        conversations() {
            return this.$store.state.directMessagesState.conversations;
        },
        directMessagesExpanded() {
            return this.$store.state.directMessagesState.expanded;
        },
        loginState() {
            return this.$store.state.loginState;
        },
        userId() {
            return this.loginState.userId;
        },
        username() {
            return this.loginState.username;
        },
        leaveGameModal() {
            return {
                message: 'Are you sure you want to leave the game?',
                options: [
                    {
                        label: 'Yes',
                        callback: this.onLeaveGameConfirmation,
                    },
                    {
                        label: 'No',
                        callback: this.onLeaveGameCancel,
                    },
                ],
            };
        },
        leaveAllGamesModal() {
            return {
                message: 'You can only be in one active game at a time. ' +
                    'Would you like to leave your other game(s) and join this one?',
                options: [
                    {
                        label: 'Yes',
                        callback: this.onLeaveAllGamesConfirmation,
                    },
                    {
                        label: 'No',
                        callback: this.onLeaveAllGamesCancel,
                    },
                ],
            };
        },
        year() {
            return moment().format('YYYY');
        },
    },
    methods: {
        onStorageEvent(storage) {
            try {
                const prevParsedStorage = JSON.parse(storage.oldValue);
                const parsedStorage = JSON.parse(storage.newValue);
                const prevLoginState = prevParsedStorage.loginState;
                const loginState = parsedStorage.loginState;

                if (loginState.sessionId &&
                    moment(loginState.sessionExp).isAfter(moment()) &&
                    this.loginState?.sessionId !== loginState.sessionId
                ) {

                    this.$store.dispatch('setAuthDetails', loginState);
                }

                if (prevLoginState.sessionId && !loginState.sessionId) {
                    this.$store.dispatch('setAuthDetails', loginInitialState);
                }
            } catch {
                // do nothing
            }
        },
        expandDirectMessages() {
            this.$store.dispatch('setExpandedDirectMessages', true);
        },
        async onLeaveGameConfirmation() {
            await this.$store.dispatch('leaveGame', this.$store.getters.leaveGameData.gameId);
            this.$store.dispatch('getRooms');
            this.$store.dispatch('closeModal', 'leaveGame');
        },
        async onLeaveAllGamesConfirmation() {
            const { dispatch, getters: { leaveAllGamesData } } = this.$store;
            const response = await dispatch('leaveAllGames');

            if (response.ok) {
                if (leaveAllGamesData.gameId) {
                    dispatch('joinRoom', leaveAllGamesData);
                } else {
                    dispatch('createGame', leaveAllGamesData);
                }

                dispatch('closeModal', 'leaveAllGames');

            } else {
                const error = await response.json();

                this.$notify({
                    group: 'app',
                    title: 'Error',
                    type: 'error',
                    text: error.message,
                });
            }
        },
        async onLeaveGameCancel() {
            this.$store.dispatch('closeModal', 'leaveGame');
        },
        async onLeaveAllGamesCancel() {
            this.$store.dispatch('closeModal', 'leaveAllGames');
        },
        showModal(type) {
            return this.$store.state.modalState.modals[type].show;
        },
		closeTermsAndConditionsModal() {
			this.$store.dispatch('closeModal', 'termsAndConditions');
		},
		openTermsAndConditionsModal() {
			this.$store.dispatch('openModal', { type: 'termsAndConditions' });
		},
    },
};
</script>

<style lang="scss">
    @use "@/styles/styles.scss" as *;

    body {
        margin: 0;
        background: $cream;
    }

    .app {
        font-family: 'RobotoSlab', 'Avenir', Helvetica, Arial, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-align: center;
        color: $dark-blue;
        display: flex;
        flex-direction: column;
        min-height: 100vh;
    }

    .main {
        display: flex;
        text-align: left;
        flex-grow: 1;
        flex-direction: column;
    }

    .main-nav-wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 25px;
        background: $dark-brown;
        box-shadow: -2px 1px 3px 1px $light-medium-gray;
    }

    .footer {
        color: $medium-extra-dark-gray;
        font-size: .8em;
        margin: 0 auto;
        max-width: 80%;
        padding: 25px;
        text-align: center;

        .copyright-notice,
		.terms-and-conditions-notice {
            margin-top: 10px;
            font-size: .8em;
            max-width: 675px;
        }

        .nav-link-icon {
            color: $sage-brown;
            cursor: pointer;
            margin-left: 5px;
        }
    }

    .wc-lock {
        height: 50px;
        width: auto;
    }

    .direct-messages-wrapper {
        position: fixed;
        bottom: 3.5%;
        right: 2.5%;
        z-index: 151;

        .unread-messages-count {
            background: $teal;
            border-radius: 50%;
            color: white;
            display: inline-block;
            font-size: .7rem;
            position: absolute;
            top: 0;
            height: 15px;
            line-height: 15px;
            padding: 2px;
            right: 0;
            transform: translate(32%, -24%);
            width: 15px;
        }
    }

    @media only screen and (min-width: $screen-sm) {

        .footer {
            max-width: 60%;
        }
    }

	.rank-notification {

		.notification-title {
			border-left: 4px solid $lancer;
			background: #e54d42;
			color: white;
			padding: 10px;
			border: none;
		}

		.notification-content {
			border-left: 4px solid $lancer;
			padding: 12px;
			background: white !important;
		}
	}

</style>
